/**
 * Semantic domain tokens for Kéves App.
 *
 * Each ministry domain exposes a full token set:
 *   gradient  — Tailwind gradient classes for DomainHero
 *   pill      — active FilterChip bg+border+text
 *   accent    — text color for numeric/contextual accents
 *   soft      — light bg for icon boxes
 *   fab       — FAB background + shadow
 *   iconColor — icon color for ActionGrid.Item and similar minimal-icon surfaces
 *
 * Brand palette (logo-derived):
 *   #0B1F3A  — deep navy (dark base)
 *   #0b3b82  — brand blue (primary)
 *   #1A7FFF  — medium blue (accent)
 *   #00C4FF  — cyan (highlight)
 *
 * Semantic domain keys (preferred going forward):
 *   pastoral      — personas, hogares, familias
 *   visits        — visitas pastorales
 *   prayer        — oraciones
 *   notes         — notas pastorales
 *   announcements — anuncios
 *   organization  — iglesias, departamentos, ministerios
 *   scheduling    — eventos, agenda
 *   teaching      — predicación, enseñanza
 *   resources     — materiales, biblioteca
 *   bible_study   — estudios bíblicos
 *   small_groups  — grupos pequeños
 *   admin         — revisión administrativa, reportes
 *
 * Legacy keys (kept for backward compatibility):
 *   district, flock, church, agenda
 *
 * Usage:
 *   import { getDomain } from "@/theme/domains";
 *   const d = getDomain("pastoral");
 *   <div className={d.soft}>...</div>
 */

export const DOMAINS = {
  // ─── Semantic (preferred) ────────────────────────────────────────────────

  /** Personas, hogares, familias — azul brand */
  pastoral: {
    gradient:  "from-[#0B1F3A] via-[#0b3b82] to-[#1A7FFF]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(29,78,216,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Visitas pastorales — azul brillante → brand → navy */
  visits: {
    gradient:  "from-[#1A7FFF] via-[#0b3b82] to-[#0B1F3A]",
    pill:      "border-blue-600 bg-blue-600 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-600 shadow-[0_6px_24px_rgba(26,127,255,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Oraciones — índigo profundo, contemplativo */
  prayer: {
    gradient:  "from-[#312e81] via-[#3730a3] to-[#4338ca]",
    pill:      "border-indigo-700 bg-indigo-700 text-white",
    accent:    "text-indigo-600 dark:text-indigo-400",
    soft:      "bg-indigo-50 dark:bg-indigo-950/30",
    fab:       "bg-indigo-700 shadow-[0_6px_24px_rgba(67,56,202,0.35)]",
    iconColor: "text-indigo-600 dark:text-indigo-400",
  },

  /** Notas pastorales — azul medio */
  notes: {
    gradient:  "from-[#1e3a5f] via-[#1e40af] to-[#2563eb]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(30,64,175,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Anuncios — navy → brand → cyan */
  announcements: {
    gradient:  "from-[#0B1F3A] via-[#0b3b82] to-[#00C4FF]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(11,59,130,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Iglesias, departamentos, ministerios */
  organization: {
    gradient:  "from-[#0b1e40] via-[#162d5e] to-[#1b3d7a]",
    pill:      "border-indigo-700 bg-indigo-700 text-white",
    accent:    "text-indigo-600 dark:text-indigo-400",
    soft:      "bg-indigo-50 dark:bg-indigo-950/30",
    fab:       "bg-indigo-700 shadow-[0_6px_24px_rgba(22,45,94,0.35)]",
    iconColor: "text-indigo-600 dark:text-indigo-400",
  },

  /** Eventos, agenda pastoral — azul brillante → brand → índigo */
  scheduling: {
    gradient:  "from-[#1A7FFF] via-[#0b3b82] to-[#312e81]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(29,78,216,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Predicación, enseñanza — brand blue */
  teaching: {
    gradient:  "from-[#0b3b82] via-[#1245a0] to-[#1b4f9c]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(11,59,130,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Estudios bíblicos — sky azul → cyan */
  bible_study: {
    gradient:  "from-[#0369a1] via-[#0284c7] to-[#00C4FF]",
    pill:      "border-sky-600 bg-sky-600 text-white",
    accent:    "text-sky-600 dark:text-sky-400",
    soft:      "bg-sky-50 dark:bg-sky-950/30",
    fab:       "bg-sky-600 shadow-[0_6px_24px_rgba(3,105,161,0.35)]",
    iconColor: "text-sky-600 dark:text-sky-400",
  },

  /** Grupos pequeños — brand → azul brillante → cyan */
  small_groups: {
    gradient:  "from-[#0b3b82] via-[#1A7FFF] to-[#00C4FF]",
    pill:      "border-blue-600 bg-blue-600 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-600 shadow-[0_6px_24px_rgba(26,127,255,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  /** Materiales, biblioteca — navy profundo */
  resources: {
    gradient:  "from-[#0B1F3A] via-[#1e3a5f] to-[#0b3b82]",
    pill:      "border-blue-800 bg-blue-800 text-white",
    accent:    "text-blue-700 dark:text-blue-300",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-800 shadow-[0_6px_24px_rgba(11,31,58,0.35)]",
    fabGlass:  "bg-white/95 dark:bg-slate-800/95 border border-blue-500/30 dark:border-blue-400/30 shadow-[0_6px_24px_rgba(0,0,0,0.10)] backdrop-blur-sm",
    iconColor: "text-blue-700 dark:text-blue-300",
  },

  /** Revisión administrativa, reportes */
  admin: {
    gradient:  "from-[#0f172a] via-[#1e293b] to-[#334155]",
    pill:      "border-slate-600 bg-slate-600 text-white",
    accent:    "text-slate-600 dark:text-slate-400",
    soft:      "bg-slate-100 dark:bg-slate-700/30",
    fab:       "bg-slate-600 shadow-[0_6px_24px_rgba(71,85,105,0.35)]",
    iconColor: "text-slate-500 dark:text-slate-400",
  },

  // ─── Legacy (backward compatible) ────────────────────────────────────────

  district: {
    gradient:  "from-[#0b3b82] via-[#1b4f9c] to-[#2563eb]",
    pill:      "border-[#1b4f9c] bg-[#1b4f9c] text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-[#1b4f9c] shadow-[0_6px_24px_rgba(27,79,156,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  flock: {
    gradient:  "from-[#0B1F3A] via-[#0b3b82] to-[#1A7FFF]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(29,78,216,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },

  church: {
    gradient:  "from-[#0b1e40] via-[#162d5e] to-[#1b3d7a]",
    pill:      "border-[#162d5e] bg-[#162d5e] text-white",
    accent:    "text-indigo-600 dark:text-indigo-400",
    soft:      "bg-indigo-50 dark:bg-indigo-950/30",
    fab:       "bg-[#162d5e] shadow-[0_6px_24px_rgba(22,45,94,0.35)]",
    iconColor: "text-indigo-600 dark:text-indigo-400",
  },

  agenda: {
    gradient:  "from-[#1A7FFF] via-[#0b3b82] to-[#312e81]",
    pill:      "border-blue-700 bg-blue-700 text-white",
    accent:    "text-blue-600 dark:text-blue-400",
    soft:      "bg-blue-50 dark:bg-blue-950/30",
    fab:       "bg-blue-700 shadow-[0_6px_24px_rgba(29,78,216,0.35)]",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
};

/** Fallback when domain is unknown */
export const DEFAULT_DOMAIN = DOMAINS.district;

/** Returns DOMAINS[domain] or DEFAULT_DOMAIN */
export function getDomain(domain) {
  return DOMAINS[domain] ?? DEFAULT_DOMAIN;
}
